<template>
  <div class="tool-page">
    <nav class="breadcrumb">
      <router-link to="/">{{ $t('nav.home') }}</router-link>
      <span class="separator">›</span>
      <span class="current">{{ $t('textDedup.title') }}</span>
    </nav>

    <div class="tool-header">
      <h1>{{ $t('textDedup.title') }}</h1>
      <p>{{ $t('textDedup.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="actions">
        <button class="btn primary" @click="dedup">{{ $t('textDedup.dedup') }}</button>
        <button class="btn" @click="copyResult" :disabled="!output">{{ $t('common.copyResult') }}</button>
        <button class="btn" @click="clearAll">{{ $t('common.clear') }}</button>
      </div>

      <div class="options">
        <label class="option-label">
          {{ $t('textDedup.splitMode') }}
          <select v-model="splitMode" class="split-select">
            <option value="line">{{ $t('textDedup.splitByLine') }}</option>
            <option value="comma">{{ $t('textDedup.splitByComma') }}</option>
            <option value="sentence">{{ $t('textDedup.splitBySentence') }}</option>
            <option value="custom">{{ $t('textDedup.splitByCustom') }}</option>
          </select>
          <input
            v-if="splitMode === 'custom'"
            v-model="customSeparator"
            class="custom-sep-input"
            :placeholder="$t('textDedup.customSepPlaceholder')"
          />
        </label>
      </div>
      <div class="options">
        <label class="option-label">
          <input type="checkbox" v-model="trimLines" />
          {{ $t('textDedup.trimLines') }}
        </label>
        <label class="option-label">
          <input type="checkbox" v-model="ignoreCase" />
          {{ $t('textDedup.ignoreCase') }}
        </label>
        <label class="option-label">
          <input type="checkbox" v-model="ignoreEmpty" />
          {{ $t('textDedup.ignoreEmpty') }}
        </label>
      </div>

      <div v-if="successMessage" class="alert success">{{ successMessage }}</div>

      <div class="editor-container">
        <div class="editor-panel">
          <label>{{ $t('textDedup.inputLabel') }}</label>
          <textarea
            v-model="input"
            :placeholder="$t('textDedup.inputPlaceholder')"
            spellcheck="false"
          ></textarea>
        </div>
        <div class="editor-panel">
          <label>{{ $t('textDedup.outputLabel') }}
            <span v-if="stats" class="stats-info">{{ stats }}</span>
          </label>
          <textarea
            v-model="output"
            readonly
            :placeholder="$t('textDedup.outputPlaceholder')"
            spellcheck="false"
          ></textarea>
        </div>
      </div>
    </div>

    <div class="tool-description">
      <h2>{{ $t('textDedup.whatIs') }}</h2>
      <p>{{ $t('textDedup.whatIsDesc') }}</p>

      <h2>{{ $t('textDedup.useCasesTitle') }}</h2>
      <ul>
        <li v-for="(item, i) in $t('textDedup.useCases')" :key="i">{{ item }}</li>
      </ul>

      <h2>{{ $t('textDedup.featuresTitle') }}</h2>
      <ul>
        <li v-for="(item, i) in $t('textDedup.features')" :key="i">{{ item }}</li>
      </ul>

      <h2>{{ $t('textDedup.tipsTitle') }}</h2>
      <ul>
        <li v-for="(item, i) in $t('textDedup.tips')" :key="i">{{ item }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TextDedup',
  data() {
    return {
      input: '',
      output: '',
      successMessage: '',
      trimLines: true,
      ignoreCase: false,
      ignoreEmpty: true,
      splitMode: 'line',
      customSeparator: '',
      stats: ''
    }
  },
  methods: {
    getSeparatorInfo() {
      switch (this.splitMode) {
        case 'line':
          return { pattern: /\n/, joiner: '\n' }
        case 'comma':
          // 匹配中英文逗号
          return { pattern: /[,，]/, joiner: '，' }
        case 'sentence':
          // 匹配中英文句号、分号、感叹号、问号等句子分隔符（保留换行也作为分隔）
          return { pattern: /[。；;！!？?\n]/, joiner: '。' }
        case 'custom': {
          if (!this.customSeparator) return { pattern: /\n/, joiner: '\n' }
          // 将用户输入的分隔符转义后用作正则
          const escaped = this.customSeparator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
          return { pattern: new RegExp(escaped), joiner: this.customSeparator }
        }
        default:
          return { pattern: /\n/, joiner: '\n' }
      }
    },
    dedup() {
      if (!this.input.trim()) return
      const { pattern, joiner } = this.getSeparatorInfo()
      const parts = this.input.split(pattern)
      const seen = new Set()
      const result = []
      let removedCount = 0

      for (let part of parts) {
        let comparePart = part
        if (this.trimLines) comparePart = comparePart.trim()
        if (this.ignoreEmpty && comparePart === '') continue
        if (this.ignoreCase) comparePart = comparePart.toLowerCase()

        if (!seen.has(comparePart)) {
          seen.add(comparePart)
          result.push(this.trimLines ? part.trim() : part)
        } else {
          removedCount++
        }
      }

      this.output = result.join(joiner)
      this.stats = this.$t('textDedup.statsInfo')
        .replace('{total}', parts.length)
        .replace('{removed}', removedCount)
        .replace('{remain}', result.length)
      this.successMessage = this.$t('textDedup.success')
      setTimeout(() => { this.successMessage = '' }, 2000)
    },
    async copyResult() {
      if (!this.output) return
      try {
        await navigator.clipboard.writeText(this.output)
        this.successMessage = this.$t('common.copied')
      } catch {
        const ta = document.createElement('textarea')
        ta.value = this.output
        ta.style.position = 'fixed'
        ta.style.left = '-9999px'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
        this.successMessage = this.$t('common.copied')
      }
      setTimeout(() => { this.successMessage = '' }, 2000)
    },
    clearAll() {
      this.input = ''
      this.output = ''
      this.stats = ''
      this.successMessage = ''
    }
  }
}
</script>

<style scoped>
.tool-page { max-width: 1200px; margin: 0 auto; }
.breadcrumb { margin-bottom: 24px; font-size: 14px; color: var(--text-secondary); }
.breadcrumb a { color: var(--accent-color); text-decoration: none; }
.breadcrumb a:hover { text-decoration: underline; }
.breadcrumb .separator { margin: 0 8px; color: var(--text-muted); }
.tool-header { text-align: center; margin-bottom: 32px; }
.tool-header h1 { font-size: 32px; margin-bottom: 12px; color: var(--text-primary); }
.tool-header p { color: var(--text-secondary); font-size: 16px; max-width: 600px; margin: 0 auto; line-height: 1.6; }
.tool-card { background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 24px; box-shadow: var(--shadow-md); }
.actions { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 16px; }
.btn { background: var(--bg-tertiary); color: var(--text-primary); border: 1px solid var(--border-color); border-radius: 8px; padding: 10px 20px; cursor: pointer; font-size: 14px; transition: all 0.2s; }
.btn:hover { background: var(--bg-hover); }
.btn.primary { background: var(--accent-color); border-color: var(--accent-color); color: white; }
.btn.primary:hover { background: var(--accent-hover); }
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.options { display: flex; gap: 20px; flex-wrap: wrap; margin-bottom: 16px; }
.option-label { display: flex; align-items: center; gap: 6px; color: var(--text-secondary); font-size: 14px; cursor: pointer; }
.option-label input[type="checkbox"] { cursor: pointer; }
.split-select { background: var(--bg-input); color: var(--text-primary); border: 1px solid var(--border-color); border-radius: 6px; padding: 4px 8px; font-size: 13px; cursor: pointer; outline: none; }
.split-select:focus { border-color: var(--accent-color); }
.custom-sep-input { background: var(--bg-input); color: var(--text-primary); border: 1px solid var(--border-color); border-radius: 6px; padding: 4px 8px; font-size: 13px; width: 100px; outline: none; }
.custom-sep-input:focus { border-color: var(--accent-color); }
.alert.success { padding: 12px 16px; border-radius: 8px; margin-bottom: 16px; background: rgba(34,197,94,0.1); border: 1px solid rgba(34,197,94,0.3); color: var(--success-color); }
.editor-container { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
@media (max-width: 768px) { .editor-container { grid-template-columns: 1fr; } }
.editor-panel { display: flex; flex-direction: column; }
.editor-panel label { font-size: 14px; color: var(--text-secondary); margin-bottom: 8px; display: flex; align-items: center; gap: 8px; }
.stats-info { font-size: 12px; color: var(--accent-color); }
.editor-panel textarea { min-height: 350px; background: var(--bg-input); border: 1px solid var(--border-color); border-radius: 8px; padding: 16px; color: var(--text-primary); font-family: 'Monaco','Menlo','Ubuntu Mono',monospace; font-size: 13px; line-height: 1.5; resize: vertical; transition: background 0.3s, border-color 0.3s; width: 100%; box-sizing: border-box; }
.editor-panel textarea:focus { outline: none; border-color: var(--accent-color); }
.tool-description { margin-top: 32px; background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 32px; box-shadow: var(--shadow-sm); }
.tool-description h2 { font-size: 20px; color: var(--text-primary); margin: 24px 0 16px; padding-bottom: 8px; border-bottom: 2px solid var(--accent-light); }
.tool-description h2:first-child { margin-top: 0; }
.tool-description p { font-size: 15px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 16px; }
.tool-description ul { padding-left: 24px; margin-bottom: 16px; }
.tool-description li { font-size: 15px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 8px; }
</style>
